import React from "react";

/**
 * Detailed pictogram icons for the Business Journey stage & support cards.
 * Style: solid silhouette, chunky, monochrome primary (soft white via
 * currentColor on the wrapping element) + a single restrained accent tone
 * for detail (passed via the `accent` prop).
 *
 * Not meant to be reused elsewhere — keep small utility icons in the rest
 * of the app on lucide-react.
 */

const DEFAULT_ACCENT = "rgb(125 211 252)"; // sky-300

const Base = ({ size = 56, className = "", children, ariaLabel }) => (
  <svg
    viewBox="0 0 32 32"
    width={size}
    height={size}
    className={className}
    role="img"
    aria-label={ariaLabel}
    aria-hidden={!ariaLabel}
    fill="none"
  >
    {children}
  </svg>
);

/* ============================================================ 01 · Promote */
export const PictogramMegaphone = ({ size, className, accent = DEFAULT_ACCENT }) => (
  <Base size={size} className={className} ariaLabel="Promote and book">
    {/* Rear handle */}
    <rect x="2" y="13" width="4" height="7" rx="1" fill="currentColor" opacity="0.7" />
    {/* Horn body */}
    <path d="M6 12 L21 6 L21 26 L6 20 Z" fill="currentColor" />
    {/* Front rim */}
    <path d="M21 6 L23 5 L23 27 L21 26 Z" fill={accent} />
    {/* Chin strap */}
    <path
      d="M12 20 L12 27"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      opacity="0.8"
    />
    {/* Sound waves */}
    <path
      d="M25.5 10.5 Q28 16 25.5 21.5"
      stroke={accent}
      strokeWidth="1.6"
      fill="none"
      strokeLinecap="round"
    />
    <path
      d="M28.5 8 Q31.5 16 28.5 24"
      stroke={accent}
      strokeWidth="1.6"
      fill="none"
      strokeLinecap="round"
      opacity="0.7"
    />
  </Base>
);

/* ============================================================ 02 · Welcome */
export const PictogramWelcomeDoor = ({ size, className, accent = DEFAULT_ACCENT }) => (
  <Base size={size} className={className} ariaLabel="Welcome and seat">
    {/* Archway door */}
    <path d="M3 28 L3 12 A6 6 0 0 1 15 12 L15 28 Z" fill="currentColor" />
    {/* Door panel line */}
    <line x1="9" y1="12" x2="9" y2="27" stroke={accent} strokeWidth="1.5" opacity="0.9" />
    {/* Door knob */}
    <circle cx="12" cy="20" r="0.9" fill={accent} />
    {/* Velvet rope stand (base) */}
    <rect x="17" y="24" width="2" height="4" rx="0.5" fill={accent} />
    <circle cx="18" cy="23" r="1.2" fill={accent} />
    {/* Host person */}
    <circle cx="25" cy="12" r="3" fill="currentColor" />
    <path d="M20 25 C 20 20, 30 20, 30 25 L30 28 L20 28 Z" fill="currentColor" />
    {/* Host bow-tie accent */}
    <path d="M23.5 16 L26.5 16 L25 17.2 Z" fill={accent} />
    <path d="M23.5 18 L26.5 18 L25 16.8 Z" fill={accent} />
  </Base>
);

/* ========================================================== 03 · Serve/POS */
export const PictogramPosTerminal = ({ size, className, accent = DEFAULT_ACCENT }) => (
  <Base size={size} className={className} ariaLabel="Serve and sell">
    {/* Receipt streaming up */}
    <path
      d="M11 8 L11 2.5 Q11 2 11.5 2 L16.5 2 Q17 2 17 2.5 L17 8 Z"
      fill="currentColor"
      opacity="0.55"
    />
    <line x1="12" y1="4" x2="16" y2="4" stroke={accent} strokeWidth="0.7" />
    <line x1="12" y1="6" x2="16" y2="6" stroke={accent} strokeWidth="0.7" />
    {/* Terminal body */}
    <rect x="3" y="8" width="26" height="20" rx="2" fill="currentColor" />
    {/* Screen */}
    <rect x="5.5" y="10" width="21" height="6" rx="1" fill={accent} />
    <rect x="7" y="12" width="10" height="1" rx="0.5" fill="currentColor" opacity="0.5" />
    <rect x="7" y="13.8" width="6" height="0.8" rx="0.4" fill="currentColor" opacity="0.3" />
    {/* Keypad */}
    <g fill={accent} opacity="0.9">
      <rect x="5.5" y="18" width="3.5" height="2.4" rx="0.4" />
      <rect x="10" y="18" width="3.5" height="2.4" rx="0.4" />
      <rect x="14.5" y="18" width="3.5" height="2.4" rx="0.4" />
      <rect x="5.5" y="21.2" width="3.5" height="2.4" rx="0.4" />
      <rect x="10" y="21.2" width="3.5" height="2.4" rx="0.4" />
      <rect x="14.5" y="21.2" width="3.5" height="2.4" rx="0.4" />
    </g>
    {/* Big pay button */}
    <rect x="19.5" y="18" width="7" height="5.6" rx="0.7" fill={accent} />
  </Base>
);

/* ========================================================== 04 · Stock */
export const PictogramStackedCrates = ({ size, className, accent = DEFAULT_ACCENT }) => (
  <Base size={size} className={className} ariaLabel="Stock and supply">
    {/* Bottom-left crate */}
    <rect x="1.5" y="18" width="12" height="10" rx="1" fill="currentColor" />
    <rect x="6.5" y="18" width="2" height="10" fill={accent} opacity="0.85" />
    <line x1="1.5" y1="23" x2="13.5" y2="23" stroke={accent} strokeWidth="0.8" />
    {/* Bottom-right crate */}
    <rect x="15.5" y="18" width="12" height="10" rx="1" fill="currentColor" opacity="0.9" />
    <rect x="20.5" y="18" width="2" height="10" fill={accent} opacity="0.7" />
    <line x1="15.5" y1="23" x2="27.5" y2="23" stroke={accent} strokeWidth="0.8" opacity="0.7" />
    {/* Top crate */}
    <rect x="8.5" y="6" width="12" height="10" rx="1" fill="currentColor" />
    <rect x="13.5" y="6" width="2" height="10" fill={accent} />
    <line x1="8.5" y1="11" x2="20.5" y2="11" stroke={accent} strokeWidth="0.8" />
    {/* Shipping label */}
    <rect x="10" y="7.5" width="3" height="1.6" rx="0.3" fill={accent} opacity="0.9" />
  </Base>
);

/* ========================================================== 05 · Close/Review */
export const PictogramRegisterChart = ({ size, className, accent = DEFAULT_ACCENT }) => (
  <Base size={size} className={className} ariaLabel="Close and review">
    {/* Register base */}
    <rect x="2" y="16" width="18" height="12" rx="1.5" fill="currentColor" />
    {/* Register display neck */}
    <rect x="6" y="10" width="10" height="6" rx="0.8" fill="currentColor" />
    {/* Display screen */}
    <rect x="7" y="11" width="8" height="4" rx="0.4" fill={accent} />
    {/* Cash slot */}
    <rect x="4" y="18.5" width="14" height="1.5" rx="0.4" fill={accent} opacity="0.85" />
    {/* Keys row 1 */}
    <g fill={accent}>
      <circle cx="5.5" cy="22.5" r="0.9" />
      <circle cx="8.5" cy="22.5" r="0.9" />
      <circle cx="11.5" cy="22.5" r="0.9" />
      <circle cx="14.5" cy="22.5" r="0.9" />
    </g>
    {/* Keys row 2 */}
    <g fill={accent} opacity="0.8">
      <circle cx="5.5" cy="25.5" r="0.9" />
      <circle cx="8.5" cy="25.5" r="0.9" />
      <circle cx="11.5" cy="25.5" r="0.9" />
      <circle cx="14.5" cy="25.5" r="0.9" />
    </g>
    {/* Analytics bars (right side) */}
    <rect x="22" y="20" width="2.4" height="8" rx="0.4" fill="currentColor" opacity="0.55" />
    <rect x="25" y="16" width="2.4" height="12" rx="0.4" fill="currentColor" opacity="0.8" />
    {/* Trend line above bars */}
    <polyline
      points="22,12 24,9 26,10.5 29.5,5"
      stroke={accent}
      strokeWidth="1.6"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="22" cy="12" r="1" fill={accent} />
    <circle cx="29.5" cy="5" r="1" fill={accent} />
  </Base>
);

/* ========================================================== Team & Access */
export const PictogramTeamBadge = ({ size, className, accent = DEFAULT_ACCENT }) => (
  <Base size={size} className={className} ariaLabel="Team and access">
    {/* Back person (right) */}
    <circle cx="21" cy="9" r="3" fill="currentColor" opacity="0.6" />
    <path
      d="M14 22 C 14 17, 28 17, 28 22 L28 24 L14 24 Z"
      fill="currentColor"
      opacity="0.6"
    />
    {/* Front person (left) */}
    <circle cx="10" cy="10" r="3.6" fill="currentColor" />
    <path
      d="M3 24 C 3 18, 17 18, 17 24 L17 27 L3 27 Z"
      fill="currentColor"
    />
    {/* ID badge lanyard */}
    <path
      d="M22 12 L22 16"
      stroke={accent}
      strokeWidth="1"
      strokeLinecap="round"
    />
    {/* ID badge */}
    <rect x="18" y="17" width="11" height="10" rx="1.2" fill={accent} />
    {/* Photo box */}
    <rect x="19.5" y="18.5" width="4" height="4" rx="0.4" fill="currentColor" opacity="0.8" />
    {/* Text lines */}
    <rect x="24.5" y="19" width="3.5" height="0.9" rx="0.3" fill="currentColor" opacity="0.75" />
    <rect x="24.5" y="20.7" width="2.6" height="0.7" rx="0.3" fill="currentColor" opacity="0.55" />
    <rect x="19.5" y="24" width="8" height="0.9" rx="0.3" fill="currentColor" opacity="0.55" />
  </Base>
);

/* ========================================================== System Settings */
export const PictogramDatabaseGear = ({ size, className, accent = DEFAULT_ACCENT }) => (
  <Base size={size} className={className} ariaLabel="System settings">
    {/* DB — top ellipse */}
    <ellipse cx="12" cy="6" rx="9" ry="3" fill="currentColor" />
    {/* Middle band */}
    <path
      d="M3 6 L3 13 A9 3 0 0 0 21 13 L21 6"
      fill="currentColor"
    />
    <path d="M3 10 A9 3 0 0 0 21 10" stroke={accent} strokeWidth="0.8" fill="none" opacity="0.5" />
    {/* Bottom band */}
    <path
      d="M3 13 L3 20 A9 3 0 0 0 21 20 L21 13"
      fill="currentColor"
      opacity="0.85"
    />
    <path d="M3 17 A9 3 0 0 0 21 17" stroke={accent} strokeWidth="0.8" fill="none" opacity="0.45" />
    {/* Gear group (bottom right) */}
    <g transform="translate(19 19)">
      {/* Gear teeth */}
      <g fill={accent}>
        <rect x="4.6" y="1" width="1.8" height="2" rx="0.3" />
        <rect x="4.6" y="9" width="1.8" height="2" rx="0.3" />
        <rect x="1" y="4.6" width="2" height="1.8" rx="0.3" />
        <rect x="9" y="4.6" width="2" height="1.8" rx="0.3" />
        {/* Diagonal teeth */}
        <rect x="1.6" y="1.6" width="2" height="2" rx="0.3" transform="rotate(45 2.6 2.6)" />
        <rect x="7.4" y="1.6" width="2" height="2" rx="0.3" transform="rotate(45 8.4 2.6)" />
        <rect x="1.6" y="7.4" width="2" height="2" rx="0.3" transform="rotate(45 2.6 8.4)" />
        <rect x="7.4" y="7.4" width="2" height="2" rx="0.3" transform="rotate(45 8.4 8.4)" />
      </g>
      {/* Gear body */}
      <circle cx="5.5" cy="5.5" r="3.6" fill={accent} />
      {/* Gear hub */}
      <circle cx="5.5" cy="5.5" r="1.5" fill="currentColor" />
    </g>
  </Base>
);
